// Hauptkomponente für die API/Exchange-Anbindung.
// Drei Sichten:
//   1) Liste der aktiven Connections + Sync-Buttons
//   2) "Neue Connection anlegen" — Börsenauswahl + Key-Formular
//   3) Premium-Hinweis falls User auf Free-Tier
//
// Den eigentlichen Sync-Import übernimmt der Parent via `onImport(rows)` —
// gleicher Flow wie beim CSV-Import.

import { useEffect, useState } from 'react';
import { F, R } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { useUserPlan } from '../context/UserPlanContext';
import ExchangeLogo from './ExchangeLogo';
import InfoBanner from './InfoBanner';
import { API_EXCHANGES } from '../utils/exchanges';
import {
  testConnection, saveConnection, listConnections,
  deleteConnection, syncConnection, ApiError,
} from '../services/exchangeApi';

// Pro Börse: welche Credential-Felder das UI abfragt + welche Anleitungs-URL.
// Source-of-Truth ist der Backend-Adapter (siehe api/_lib/adapters/index.js).
// `available` = Adapter bereits implementiert. False = Coming Soon.
const EXCHANGE_META = {
  bitpanda: { fields: ['apiKey'],                       available: true,  docsUrl: 'https://web.bitpanda.com/apikey' },
  binance:  { fields: ['apiKey', 'secret'],             available: true,  docsUrl: 'https://www.binance.com/en/my/settings/api-management' },
  coinbase: { fields: ['apiKey', 'secret'],             available: false, docsUrl: 'https://www.coinbase.com/settings/api' },
  kraken:   { fields: ['apiKey', 'secret'],             available: true,  docsUrl: 'https://www.kraken.com/u/security/api' },
  bitvavo:  { fields: ['apiKey', 'secret'],             available: true,  docsUrl: 'https://account.bitvavo.com/user/api' },
  bitget:   { fields: ['apiKey', 'secret', 'passphrase'], available: false, docsUrl: 'https://www.bitget.com/de/account/newapi' },
  bybit:    { fields: ['apiKey', 'secret'],             available: false, docsUrl: 'https://www.bybit.com/app/user/api-management' },
  okx:      { fields: ['apiKey', 'secret', 'passphrase'], available: false, docsUrl: 'https://www.okx.com/account/my-api' },
  kucoin:   { fields: ['apiKey', 'secret', 'passphrase'], available: false, docsUrl: 'https://www.kucoin.com/account/api' },
};

export default function ExchangeApiConnect({ onBack, onImport, onOpenPricing }) {
  const { T } = useTheme();
  const { t } = useT();
  const { isPremium } = useUserPlan();
  const [view, setView] = useState('list'); // 'list' | 'new'
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);     // connectionId der gerade syncenden Connection
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [recon, setRecon] = useState(null);   // { tone, text } — Saldo-Abgleich

  // ── Connections laden ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await listConnections();
        if (!cancelled) setConnections(list);
      } catch (err) {
        if (!cancelled) setError(err.message || t('con_load_connections_failed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshList = async () => {
    try {
      const list = await listConnections();
      setConnections(list);
    } catch (err) {
      setError(err.message || t('con_load_connections_failed'));
    }
  };

  // ── Sync ausführen ─────────────────────────────────────────────────────────
  const handleSync = async (conn) => {
    setError(null);
    setInfo(null);
    setRecon(null);
    setBusy(conn.id);
    try {
      const result = await syncConnection(conn.id);
      const count = result.transactions?.length || 0;
      if (count > 0 && onImport) {
        await onImport(result.transactions);
      }
      const { trades = 0, walletTx = 0, ledger = 0, skipped = 0 } = result.stats || {};
      const warningsSuffix = result.warnings?.length
        ? '\n' + t('con_sync_warning', { warnings: result.warnings.join(' ') })
        : '';
      setInfo(
        t('con_sync_success', { count, trades, ledger: walletTx || ledger, skipped })
        + warningsSuffix
      );
      setRecon(buildReconNotice(result.reconciliation, t));
      await refreshList();
    } catch (err) {
      if (err instanceof ApiError && err.requiresUpgrade) {
        onOpenPricing?.();
        return;
      }
      setError(err.message || t('con_sync_failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (conn) => {
    if (!window.confirm(t('con_delete_confirm', { label: conn.label }))) {
      return;
    }
    setError(null);
    try {
      await deleteConnection(conn.id);
      await refreshList();
      setInfo(t('con_connection_removed'));
    } catch (err) {
      setError(err.message || t('con_delete_failed'));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 16px 100px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.text, fontFamily: F.family, marginBottom: 6 }}>
        {t('con_exchange_api')}
      </div>
      <div style={{ fontSize: 13, color: T.muted, fontFamily: F.family, marginBottom: 18 }}>
        {t('con_exchange_api_sub')}
      </div>

      {!isPremium && (
        <div style={{ marginBottom: 16 }}>
          <InfoBanner tone="warning" title={t('con_premium_feature')}>
            {t('con_api_sync_premium')}{' '}
            <button
              onClick={onOpenPricing}
              style={{
                background: 'none', border: 'none', color: T.red,
                fontFamily: F.family, fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', padding: 0, textDecoration: 'underline',
              }}
            >{t('con_view_plans')}</button>
          </InfoBanner>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 12 }}>
          <InfoBanner tone="danger" title={t('con_error')}>{error}</InfoBanner>
        </div>
      )}
      {info && (
        <div style={{ marginBottom: 12 }}>
          <InfoBanner tone="success" title="">{info}</InfoBanner>
        </div>
      )}
      {recon && (
        <div style={{ marginBottom: 12 }}>
          <InfoBanner tone={recon.tone} title="">{recon.text}</InfoBanner>
        </div>
      )}

      {view === 'list' && (
        <ConnectionsList
          T={T}
          t={t}
          loading={loading}
          connections={connections}
          busyId={busy}
          onSync={handleSync}
          onDelete={handleDelete}
          onAddNew={() => { setError(null); setInfo(null); setView('new'); }}
        />
      )}

      {view === 'new' && (
        <NewConnectionForm
          T={T}
          t={t}
          onCancel={() => setView('list')}
          onSaved={async () => {
            await refreshList();
            setInfo(t('con_connection_saved'));
            setView('list');
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      <button onClick={onBack} style={{
        width: '100%', padding: '13px', background: 'none', color: T.muted,
        border: `1px solid ${T.border}`, borderRadius: R.md, fontFamily: F.family,
        fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 18,
      }}>{t('back')}</button>
    </div>
  );
}

// ── Sub-Komponente: Liste der Connections ─────────────────────────────────────

function ConnectionsList({ T, t, loading, connections, busyId, onSync, onDelete, onAddNew }) {
  if (loading) {
    return (
      <div style={{ color: T.muted, fontSize: 13, fontFamily: F.family, padding: 20, textAlign: 'center' }}>
        {t('con_loading_connections')}
      </div>
    );
  }

  return (
    <>
      {connections.length === 0 ? (
        <div style={{
          background: T.surface, border: `1px dashed ${T.border}`, borderRadius: R.lg,
          padding: 24, textAlign: 'center', marginBottom: 16,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: F.family, marginBottom: 4 }}>
            {t('con_no_connections')}
          </div>
          <div style={{ fontSize: 12.5, color: T.muted, fontFamily: F.family }}>
            {t('con_no_connections_sub')}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {connections.map(c => (
            <ConnectionRow
              key={c.id}
              T={T}
              t={t}
              conn={c}
              busy={busyId === c.id}
              onSync={() => onSync(c)}
              onDelete={() => onDelete(c)}
            />
          ))}
        </div>
      )}

      <button onClick={onAddNew} style={{
        width: '100%', padding: '13px', background: T.surface, color: T.text,
        border: `1px solid ${T.border}`, borderRadius: R.md, fontFamily: F.family,
        fontSize: 14, fontWeight: 700, cursor: 'pointer',
      }}>{t('con_connect_new_exchange')}</button>
    </>
  );
}

function ConnectionRow({ T, t, conn, busy, onSync, onDelete }) {
  const labelOfExchange = API_EXCHANGES.find(e => e.id === conn.exchangeId)?.label || conn.exchangeId;
  const lastSync = conn.lastSyncAt
    ? new Date(conn.lastSyncAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
    : t('con_never');
  const statusColor = conn.lastSyncStatus === 'error' ? '#E4544A'
                    : conn.lastSyncStatus === 'ok'    ? '#2ECC71'
                    : T.muted;

  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: R.lg, padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ExchangeLogo name={labelOfExchange} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: F.family }}>
            {conn.label || labelOfExchange}
          </div>
          <div style={{ fontSize: 11.5, color: statusColor, fontFamily: F.family, marginTop: 2 }}>
            {t('con_last_sync', { time: lastSync })}
            {conn.lastSyncError ? ` — ${conn.lastSyncError}` : ''}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={onSync} disabled={busy} style={{
          flex: 1, padding: '10px', background: T.red, color: '#fff',
          border: 'none', borderRadius: R.sm, fontFamily: F.family,
          fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}>{busy ? t('con_syncing') : t('con_sync_now')}</button>
        <button onClick={onDelete} disabled={busy} style={{
          padding: '10px 14px', background: 'none', color: T.muted,
          border: `1px solid ${T.border}`, borderRadius: R.sm, fontFamily: F.family,
          fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
        }}>{t('con_remove')}</button>
      </div>
    </div>
  );
}

// ── Sub-Komponente: Neue Connection anlegen ───────────────────────────────────

function NewConnectionForm({ T, t, onCancel, onSaved, onError }) {
  const [exchangeId, setExchangeId] = useState(null);
  const [label, setLabel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, error}

  // Alle Börsen anzeigen, aber nicht-verfügbare als disabled markieren
  const exchangesToShow = API_EXCHANGES.filter(e => EXCHANGE_META[e.id]);
  const meta = exchangeId ? EXCHANGE_META[exchangeId] : null;
  const fields = meta?.fields || ['apiKey', 'secret'];
  const needsSecret     = fields.includes('secret');
  const needsPassphrase = fields.includes('passphrase');
  const canSubmit = exchangeId
                    && apiKey.length >= 8
                    && (!needsSecret     || secret.length     >= 8)
                    && (!needsPassphrase || passphrase.length >= 1);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testConnection({
        exchangeId, apiKey, secret,
        passphrase: needsPassphrase ? passphrase : null,
      });
      setTestResult(res);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveConnection({
        exchangeId,
        label: label.trim() || null,
        apiKey, secret,
        passphrase: needsPassphrase ? passphrase : null,
      });
      onSaved();
    } catch (err) {
      onError(err.message || t('con_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {!exchangeId ? (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, fontFamily: F.family, marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {t('con_choose_exchange')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {exchangesToShow.map(ex => {
              const m = EXCHANGE_META[ex.id];
              const enabled = m?.available;
              return (
                <button
                  key={ex.id}
                  onClick={() => enabled && setExchangeId(ex.id)}
                  disabled={!enabled}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    background: T.surface, border: `1px solid ${T.border}`,
                    borderRadius: R.md,
                    cursor: enabled ? 'pointer' : 'not-allowed',
                    opacity: enabled ? 1 : 0.5,
                    textAlign: 'left',
                  }}>
                  <ExchangeLogo name={ex.label} size={28} />
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.text, fontFamily: F.family }}>{ex.label}</span>
                  {!enabled && (
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, color: T.muted, fontFamily: F.family,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>{t('con_soon')}</span>
                  )}
                </button>
              );
            })}
          </div>
          <button onClick={onCancel} style={{
            width: '100%', padding: '12px', background: 'none', color: T.muted,
            border: `1px solid ${T.border}`, borderRadius: R.md, fontFamily: F.family,
            fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
          }}>{t('cancel')}</button>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <ExchangeLogo name={API_EXCHANGES.find(e => e.id === exchangeId)?.label} size={32} />
            <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: T.text, fontFamily: F.family }}>
              {API_EXCHANGES.find(e => e.id === exchangeId)?.label}
            </div>
            <button onClick={() => { setExchangeId(null); setTestResult(null); }} style={{
              background: 'none', border: 'none', color: T.muted,
              fontSize: 12, fontFamily: F.family, cursor: 'pointer', textDecoration: 'underline',
            }}>{t('con_choose_other')}</button>
          </div>

          <InfoBanner tone="info" title={t('con_create_readonly_key')}>
            {t('con_readonly_important_pre')}<strong>{t('con_readonly_key_strong')}</strong>{t('con_readonly_important_post')}
            <br />
            <a href={meta?.docsUrl} target="_blank" rel="noopener noreferrer"
               style={{ color: T.red, fontWeight: 700, textDecoration: 'underline' }}>
              {t('con_open_guide')}
            </a>
          </InfoBanner>

          {exchangeId === 'binance' && (
            <div style={{ marginTop: 10 }}>
              <InfoBanner tone="info" title="">{t('con_scope_note_binance')}</InfoBanner>
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <FieldLabel T={T}>{t('con_designation_optional')}</FieldLabel>
            <input value={label} onChange={e => setLabel(e.target.value)}
              placeholder={t('con_designation_placeholder', { exchange: API_EXCHANGES.find(e => e.id === exchangeId)?.label })}
              style={inputStyle(T)} />

            <FieldLabel T={T}>{t('con_api_key')}</FieldLabel>
            <input value={apiKey} onChange={e => setApiKey(e.target.value)}
              type="text" autoComplete="off" spellCheck="false"
              placeholder={t('con_api_key_placeholder')} style={inputStyle(T)} />

            {needsSecret && (
              <>
                <FieldLabel T={T}>{t('con_api_secret')}</FieldLabel>
                <input value={secret} onChange={e => setSecret(e.target.value)}
                  type="password" autoComplete="off" spellCheck="false"
                  placeholder="••••••••••••••••" style={inputStyle(T)} />
              </>
            )}

            {needsPassphrase && (
              <>
                <FieldLabel T={T}>{t('con_passphrase')}</FieldLabel>
                <input value={passphrase} onChange={e => setPassphrase(e.target.value)}
                  type="password" autoComplete="off" spellCheck="false"
                  placeholder={t('con_passphrase_placeholder')} style={inputStyle(T)} />
              </>
            )}
          </div>

          {testResult && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <InfoBanner tone={testResult.ok ? 'success' : 'danger'} title={testResult.ok ? t('con_connection_ok') : t('con_connection_failed')}>
                {testResult.ok ? t('con_credentials_work') : testResult.error}
              </InfoBanner>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={handleTest} disabled={!canSubmit || testing || saving} style={{
              flex: 1, padding: '12px', background: T.surface, color: T.text,
              border: `1px solid ${T.border}`, borderRadius: R.md, fontFamily: F.family,
              fontSize: 13.5, fontWeight: 700,
              cursor: (!canSubmit || testing || saving) ? 'not-allowed' : 'pointer',
              opacity: (!canSubmit || testing) ? 0.55 : 1,
            }}>{testing ? t('con_testing') : t('con_test')}</button>
            <button onClick={handleSave} disabled={!canSubmit || saving || testing} style={{
              flex: 1, padding: '12px', background: T.red, color: '#fff',
              border: 'none', borderRadius: R.md, fontFamily: F.family,
              fontSize: 13.5, fontWeight: 700,
              cursor: (!canSubmit || saving || testing) ? 'wait' : 'pointer',
              opacity: (!canSubmit || saving) ? 0.55 : 1,
            }}>{saving ? t('con_saving') : t('save')}</button>
          </div>

          <button onClick={onCancel} style={{
            width: '100%', padding: '12px', background: 'none', color: T.muted,
            border: `1px solid ${T.border}`, borderRadius: R.md, fontFamily: F.family,
            fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 10,
          }}>{t('cancel')}</button>
        </>
      )}
    </div>
  );
}

// Saldo-Abgleich (Reconciliation) → Banner-Notice oder null.
function buildReconNotice(rec, t) {
  if (!rec) return null;
  if (rec.skipped) return { tone: 'info', text: t('con_reconcile_partial') };
  if (rec.ok) return { tone: 'success', text: t('con_reconcile_ok', { matches: rec.matches || 0 }) };
  const fmt = (n) => Number(n).toLocaleString('de-DE', { maximumFractionDigits: 8 });
  const detail = (rec.mismatches || [])
    .slice(0, 5)
    .map(m => `${m.sym} (${fmt(m.computed)} / ${fmt(m.reported)})`)
    .join(', ');
  return { tone: 'warning', text: t('con_reconcile_mismatch', { count: rec.mismatches?.length || 0, detail }) };
}

function FieldLabel({ T, children }) {
  return (
    <div style={{
      fontSize: 10.5, color: T.muted, fontFamily: F.family,
      textTransform: 'uppercase', letterSpacing: '0.07em',
      marginBottom: 4, marginTop: 12,
    }}>{children}</div>
  );
}

function inputStyle(T) {
  return {
    width: '100%', padding: '10px 12px', background: T.surfaceHigh || T.surface,
    border: `1px solid ${T.border}`, borderRadius: R.sm, color: T.text,
    fontFamily: F.family, fontSize: 13, boxSizing: 'border-box',
    marginBottom: 4,
  };
}
